import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA, TextCTA } from "@/components/CTA";
import { Tick } from "@/components/Annotation";
import { SectionLabel, Tape } from "@/components/Notebook";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

export default function ExecutiveCoaching() {
  const { t, l } = useLang();

  const services = [
    {
      key: "hspc",
      label: t("coaching.svc_hspc_label"),
      title: t("coaching.svc_hspc_title"),
      intro: t("coaching.svc_hspc_intro"),
      duration: t("coaching.svc_hspc_duration"),
      forLabel: t("coaching.svc_hspc_for_label"),
      forList: t("coaching.svc_hspc_for"),
      href: ROUTES.highStakesPresentationCoaching,
      cta: t("coaching.svc_hspc_cta"),
    },
    {
      key: "epd",
      label: t("coaching.svc_epd_label"),
      title: t("coaching.svc_epd_title"),
      intro: t("coaching.svc_epd_intro"),
      duration: t("coaching.svc_epd_duration"),
      forLabel: t("coaching.svc_epd_for_label"),
      forList: t("coaching.svc_epd_for"),
      href: ROUTES.executivePresenceDevelopment,
      cta: t("coaching.svc_epd_cta"),
    },
  ];

  const steps = [
    { t: t("coaching.process_step_1_t"), d: t("coaching.process_step_1_d") },
    { t: t("coaching.process_step_2_t"), d: t("coaching.process_step_2_d") },
    { t: t("coaching.process_step_3_t"), d: t("coaching.process_step_3_d") },
  ];

  return (
    <>
      <PageMeta
        titleKey="meta.coaching_title"
        descriptionKey="meta.coaching_description"
        path="/executive-coaching"
      />
      <PageHero
        testId="ec-hero"
        eyebrow={t("coaching.hero_eyebrow")}
        headline={[t("coaching.hero_h1_a"), t("coaching.hero_h1_b")]}
        emphasis={t("coaching.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("coaching.hero_intro")}
      >
        <PrimaryCTA href={ROUTES.booking} testId="ec-book-cta" location="hero" targetService="general_booking">
          {t("coaching.book_cta")}
        </PrimaryCTA>
        <OutlineCTA href={ROUTES.nextPresentationReadinessCheck} testId="ec-diagnostic-cta" location="hero" targetAssetId="next_presentation_readiness">
          {t("cta.take_diagnostic")}
        </OutlineCTA>
      </PageHero>

      {/* decision helper */}
      <section
        className="relative z-10 py-14 border-y border-ink/12 bg-paper-deep/40"
        data-testid="ec-decision"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-hand text-terracotta text-2xl md:text-3xl leading-tight">
              {t("coaching.decision_hand")}
            </p>
            <p className="mt-3 font-body text-ink-soft text-base md:text-lg">
              <span className="font-serif italic text-ink">
                {t("coaching.decision_yes_label")}
              </span>
              {t("coaching.decision_yes_body")}{" "}
              <span className="font-serif italic text-ink">
                {t("coaching.decision_no_label")}
              </span>
              {t("coaching.decision_no_body")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* the two services */}
      <section className="relative z-10 py-24 md:py-36" data-testid="ec-services">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-8 md:gap-10">
          {services.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.1}>
              <article className="relative h-full bg-paper border border-ink/12 shadow-[3px_6px_28px_rgba(27,27,24,0.08)] p-8 md:p-10">
                <Tape className="absolute -top-4 left-8" rotate={-5} width={120} />
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
                  {s.label}
                </p>
                <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                  {s.title}
                </h2>
                <p className="mt-5 font-body text-ink-soft text-lg leading-relaxed">{s.intro}</p>
                <p className="mt-5 font-hand text-mustard text-lg">— {s.duration}</p>
                <SectionLabel className="mt-8 mb-4 block">{s.forLabel}</SectionLabel>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  {Array.isArray(s.forList) &&
                    s.forList.map((it) => (
                      <li key={it} className="flex items-start gap-3">
                        <Tick className="w-4 h-4 mt-1 shrink-0" />
                        <span className="font-body text-ink">{it}</span>
                      </li>
                    ))}
                </ul>
                <div className="mt-8">
                  <Link
                    to={l(s.href)}
                    data-testid={`ec-${s.key}-link`}
                    className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.22em] text-ink hover:text-terracotta transition-colors"
                  >
                    {s.cta}
                    <span>&rarr;</span>
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Communication under pressure */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ec-pressure">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10 md:mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("coaching.pressure_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("coaching.pressure_h_a")}
              <br />
              <span className="italic">{t("coaching.pressure_h_b")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("coaching.pressure_body")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <Tape className="absolute -top-4 left-16 z-10" rotate={-4} width={130} />
              <Tape className="absolute -top-4 right-16 z-10" rotate={5} width={110} />
              <div className="bg-paper border border-ink/12 shadow-[8px_16px_56px_rgba(27,27,24,0.12)] p-3 md:p-5">
                <img
                  src="/sketches/epd-pressure.webp"
                  loading="lazy"
                  alt={t("coaching.pressure_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ec-pressure-image"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* how the engagement works */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="ec-process"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("coaching.process_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("coaching.process_h_a")}
              <br />
              <span className="italic">{t("coaching.process_h_b")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <StaggerItem key={i}>
                <div>
                  <span className="font-serif text-6xl text-terracotta/70 leading-none block mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-2xl text-ink mb-3">{step.t}</h3>
                  <p className="font-body text-ink-soft leading-relaxed">{step.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="ec-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-paper">
              {t("coaching.final_h")}
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto">
              {t("coaching.final_body")}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.booking} testId="ec-final-book-cta" location="final_cta" targetService="general_booking">
              {t("coaching.book_cta")}
            </PrimaryCTA>
            <TextCTA
              href={ROUTES.contact}
              className="!text-paper/90 hover:!text-mustard"
              testId="ec-final-contact-cta"
              location="final_cta"
              targetAssetId="contact_request"
            >
              {t("coaching.final_secondary")}
            </TextCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
